import api from './api'
import { Order, OrderPaymentMethod } from '../types/Order/Order'
import { CurrencyEnum } from '../types/Currency'
import { OngoingOrder } from '../types/Order/OngoingOrder'
import { QueueName } from '../types/Order/Queue'
import { getLocalePathFromPathname } from '../lib/utils/language'
import routes from '../config/routes'

export interface InitPaymentResult {
  redirectUrl: string | null
  orderId: string
  needTransactionConfirmation: boolean
  receiverAddress: string | null
  tokenIdentifier: string | null
  amountInBaseUnits: string | null
  wireTransferReference: string | null
}

export interface ConfirmOnchainPaymentResult {
  status: 'success' | 'processing' | 'invalid'
  txHash: string
}

const OrderService = {
  async getOrderById(orderId: string): Promise<Order> {
    const { data } = await api.get(`/v1/users/me/orders/${orderId}`)
    return data
  },
  async joinQueue(
    propertyId: string,
    desiredFractionQuantity: number,
  ): Promise<OngoingOrder> {
    const { data } = await api.post('/v1/users/me/orders/queue/join', {
      propertyId,
      quantity: desiredFractionQuantity,
    })
    return data
  },
  async initPayment(
    orderId: string,
    propertyId: string,
    walletId: string,
    billingAddressId: string,
    desiredFractionQuantity: number,
    priceDisplayed: number,
    currency: CurrencyEnum,
    paymentMethod: OrderPaymentMethod,
    locale: string,
  ): Promise<InitPaymentResult> {
    const successUrl =
      process.env.NEXT_PUBLIC_BASEURL +
      getLocalePathFromPathname('/orders/' + orderId + '/sign', locale)
    const cancelUrl =
      process.env.NEXT_PUBLIC_BASEURL +
      getLocalePathFromPathname(routes.properties + `${propertyId}`, locale)

    const { data } = await api.post('/v1/users/me/orders/init-payment', {
      orderId,
      walletId,
      propertyId,
      billingAddressId,
      quantity: desiredFractionQuantity,
      priceDisplayed,
      currency,
      paymentMethod,
      successUrl,
      cancelUrl,
    })
    return data
  },
  async confirmOnchainPayment(
    orderId: string,
    txHash: string,
  ): Promise<ConfirmOnchainPaymentResult> {
    const { data } = await api.post(
      `/v1/users/me/orders/${orderId}/confirm-onchain-payment`,
      { txHash },
    )

    return data
  },
  async reconcileBalancePayment(orderId: string): Promise<void> {
    await api.post(`/v1/users/me/orders/${orderId}/reconcile-balance-payment`)
  },
  async confirmWireTransferSent(orderId: string): Promise<{
    status: string
    orderId: string
    wireTransferReference: string
  }> {
    const { data } = await api.post(
      `/v1/users/me/orders/${orderId}/wire-transfer/sent`,
    )
    return data
  },
  async getOngoingOrder(propertyId: string): Promise<OngoingOrder> {
    const { data } = await api.post('/v1/users/me/orders/status', {
      propertyId,
    })
    return data
  },
  async leaveQueue(propertyId: string, queueName: QueueName): Promise<void> {
    await api.post(`/v1/users/me/orders/queue/leave`, {
      propertyId,
      queueName,
    })
  },
  async renounceStockLock(propertyId: string, orderId: string): Promise<void> {
    await api.post(`/v1/users/me/orders/stock-lock/renounce`, {
      propertyId,
      orderId,
    })
  },
  async getSignatureLink(
    orderId: string,
    returnUrl?: string,
  ): Promise<{ url: string | null; completed: boolean }> {
    const { data } = await api.get(
      `/v1/users/me/orders/${orderId}/signature-link`,
      {
        params: { returnUrl },
      },
    )

    return {
      url: data.url ?? null,
      completed: data.completed ?? false,
    }
  },
}

export default OrderService
